import type { ReactNode } from "react";
import type { LucideIcon } from "lucide-react";
import { SectionHeader } from "./SectionHeader";

interface CalculatorTemplateProps {
  inputsSection: ReactNode;
  resultsSection: ReactNode;
  chartSection?: ReactNode;
  /** Optional label + icon above chart (e.g. "Growth over time") */
  chartSectionLabel?: string;
  chartSectionIcon?: LucideIcon;
  /** Short intro (100–150 words) below the calculator, defining the topic and teasing formula/examples */
  introSection?: ReactNode;
  /** Main explanation (~300–400 words): formula, example table, limitations. Visible prose, not accordion. */
  explanationSection?: ReactNode;
  /** Optional label + icon above explanation (e.g. "Formula & examples") */
  explanationSectionLabel?: string;
  explanationSectionIcon?: LucideIcon;
  /** @deprecated Use explanationSection for main content. Kept for backwards compatibility. */
  accordionSection?: ReactNode;
}

export function CalculatorTemplate({
  inputsSection,
  resultsSection,
  chartSection,
  chartSectionLabel,
  chartSectionIcon,
  introSection,
  explanationSection,
  explanationSectionLabel,
  explanationSectionIcon,
  accordionSection,
}: CalculatorTemplateProps) {
  return (
    <div className="space-y-8 pb-8">
      {/* Two-column: inputs left, results right (stacked on mobile) */}
      <div className="grid gap-8 lg:grid-cols-2">
        <div className="space-y-4">{inputsSection}</div>
        <div className="space-y-4">{resultsSection}</div>
      </div>

      {/* Chart section */}
      {chartSection && (
        <div className="rounded-xl border border-border bg-card p-4 md:p-6">
          {chartSectionLabel && chartSectionIcon && (
            <SectionHeader icon={chartSectionIcon} as="h2">
              {chartSectionLabel}
            </SectionHeader>
          )}
          {chartSection}
        </div>
      )}

      {/* Intro paragraph below calculator */}
      {introSection && (
        <div className="space-y-3 text-base text-foreground [&_p]:leading-relaxed">
          {introSection}
        </div>
      )}

      {/* Main explanation: formula, example, limitations */}
      {explanationSection && (
        <article className="space-y-6 rounded-xl border border-border bg-card p-4 md:p-6">
          {explanationSectionLabel && explanationSectionIcon && (
            <SectionHeader icon={explanationSectionIcon} as="h2">
              {explanationSectionLabel}
            </SectionHeader>
          )}
          {explanationSection}
        </article>
      )}

      {/* Legacy accordion (deprecated) */}
      {accordionSection && !explanationSection && (
        <div className="space-y-4">
          <h2 className="text-lg font-semibold text-foreground">
            How it works
          </h2>
          {accordionSection}
        </div>
      )}
    </div>
  );
}
